/** One catalog row: a loadable thing (skill, command, memory) the model can pull by name. */
export interface CatalogEntry { name: string; description?: string; path?: string }

export interface CatalogOptions {
  /** Heading line (e.g. `## Skills …`). */
  title: string;
  /** Closing line (how to load an entry). */
  footer?: string;
  /** Max UTF-8 bytes for the whole block (default 6000). Names are never dropped, so a huge set may exceed it. */
  budgetBytes?: number;
  /** Prefix shown before each name (e.g. `/` for commands). */
  namePrefix?: string;
}

export const DEFAULT_CATALOG_BUDGET = 6000;

const enc = new TextEncoder();
const bytes = (s: string) => enc.encode(s).length;

/** Cut to at most `n` chars (code points), marking the cut with `…`. */
function clip(s: string, n: number): string {
  const cps = [...s];
  if (cps.length <= n) return s;
  return n <= 1 ? '…' : cps.slice(0, n - 1).join('').trimEnd() + '…';
}

/**
 * Render a full, stable catalog: every entry as `- **name** — desc (\`path\`)`.
 * Over `budgetBytes`, descriptions are trimmed evenly (one shared max length), then — if even
 * empty descriptions don't fit — names only. Never drops a name; output depends only on the
 * entries (no task hint), so the prompt prefix stays byte-identical across turns/tasks.
 */
export function formatCatalog(entries: CatalogEntry[], opts: CatalogOptions): string {
  const budget = opts.budgetBytes ?? DEFAULT_CATALOG_BUDGET;
  const pre = opts.namePrefix ?? '';
  const wrap = (body: string) => [opts.title, body, opts.footer].filter((x) => x !== undefined && x !== '').join('\n');
  const render = (max: number) => wrap(entries.map((e) => {
    const d = (e.description ?? '').replace(/\s+/g, ' ').trim();
    const desc = d && max > 0 ? ` — ${clip(d, max)}` : '';
    return `- **${pre}${e.name}**${desc}${e.path ? ` (\`${e.path}\`)` : ''}`;
  }).join('\n'));

  const longest = Math.max(0, ...entries.map((e) => [...(e.description ?? '')].length));
  const full = render(longest);
  if (bytes(full) <= budget) return full;
  if (bytes(render(0)) <= budget) {
    // Largest shared description cap that fits (render size is monotonic in the cap).
    let lo = 0, hi = longest;
    while (lo < hi) { const mid = Math.ceil((lo + hi) / 2); if (bytes(render(mid)) <= budget) lo = mid; else hi = mid - 1; }
    return render(lo);
  }
  const names = wrap(entries.map((e) => `- **${pre}${e.name}**`).join('\n'));
  if (bytes(names) <= budget) return names;
  return wrap(`- ${entries.map((e) => pre + e.name).join(', ')}`); // densest form; may exceed budget rather than drop names
}
